import React from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { SEmbedSessionResponse } from "@norma-bi/bi-api";
import { useLocation, useNavigate } from "react-router";
import { CHAT_PATH } from "@/constants";
import { useThemeStore } from "@/theme";
import { getPosthogConfig } from "@/posthogConfig";
import {
  isSEmbedHostMessage,
  type SEmbedThemeMessage,
  type SEmbedTokenMessage,
} from "@norma-bi/s-embed-sdk";
import type { SEmbedConfig } from "@norma-bi/bi-api";
import { consumeSEmbedAnalyticsMessage } from "./hostAnalytics";
import { sEmbedAppPath, sHostSettingsPath, sNavigationHostPath } from "./fullAppPaths";

const LANGUAGE_STORAGE_KEY = "s-embed-language";
// How long S's page may take to start once it has loaded before BI shows the error screen.
export const S_START_TIMEOUT_MS = 30_000;

/**
 * The S interface language picked in S's profile menu and saved in this browser. Until the
 * user picks one, it follows the browser's first English or Arabic language.
 */
export function savedSLanguage(): "en" | "ar" {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // Blocked storage falls back to the browser language.
  }
  const language =
    saved ??
    navigator.languages
      .map((tag) => tag.toLowerCase().split("-")[0])
      .find((tag) => tag === "en" || tag === "ar");
  return language === "ar" ? "ar" : "en";
}

export type UseSEmbedParams = {
  // Full query state for embed session. Hook derives iframe URL, trusted origin,
  // bootstrap token payload, and loading-driven reset behavior from this object.
  embedSession: UseQueryResult<SEmbedSessionResponse>;
  configFallback?: SEmbedConfig;
  onBusinessSelect: (id: string) => Promise<void>;
  canPostToken: () => boolean;
};

// The S tokens' current page colour, which follows BI's theme. S accepts only hex colours.
function pageBackground() {
  const value = getComputedStyle(document.documentElement).getPropertyValue("--background").trim();
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value) ? value : null;
}

export function useSEmbed({
  embedSession,
  configFallback,
  onBusinessSelect,
  canPostToken,
}: UseSEmbedParams): {
  isReady: boolean;
  /** S's page loaded but never started (no `S_EMBED_READY`), such as an error page from S. */
  stalled: boolean;
  iframeProps: React.IframeHTMLAttributes<HTMLIFrameElement> & {
    ref: React.RefObject<HTMLIFrameElement | null>;
  };
} {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // `?logout` belongs to BI's sign-out dialog: S's first navigation message would drop it.
  const initialDeepPath = React.useRef(
    `${location.pathname.slice(CHAT_PATH.length)}${withoutLogout(location.search)}${location.hash}`,
  );
  const currentLocation = React.useRef(location);
  currentLocation.current = location;
  const [isReady, setIsReady] = React.useState(false);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  // S sent S_EMBED_READY: its app started. A loaded page that never does is an error from S.
  const [started, setStarted] = React.useState(false);
  const [stalled, setStalled] = React.useState(false);
  // BI owns the theme. S loads with it and gets later changes as S_EMBED_THEME, so a theme
  // change or a session switch never reloads S.
  const theme = useThemeStore((state) => state.theme);
  const [loadTheme] = React.useState(theme);
  const config = embedSession.data?.config ?? configFallback;
  const session = embedSession.data?.session;
  const iframeSrc = React.useMemo(() => {
    if (!config) return undefined;
    const url = new URL(`${sEmbedAppPath(config)}${initialDeepPath.current}`, config.webUiUrl);
    // S reads language and direction separately; Foodics shows Arabic right to left.
    const language = savedSLanguage();
    url.searchParams.set("locale", language);
    url.searchParams.set("dir", language === "ar" ? "rtl" : "ltr");
    url.searchParams.set("theme", loadTheme);
    // S paints its loading screens in BI's page colour until the app's appearance applies.
    const background = pageBackground();
    if (background) url.searchParams.set("background", background);
    return url.toString();
  }, [config, loadTheme]);
  const iframeOrigin = config ? new URL(config.webUiUrl).origin : null;
  const sessionMessage = React.useMemo<SEmbedTokenMessage | null>(() => {
    if (!session) {
      return null;
    }
    const posthog = getPosthogConfig();
    return {
      type: "S_EMBED_TOKEN",
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      expiresIn: session.expiresIn,
      dataContext: embedSession.data?.dataContext,
      host: {
        navigation: "host",
        overlays: "host",
        theme: "host",
      },
      ...(posthog ? { posthog } : {}),
    };
  }, [session, embedSession.data?.dataContext]);

  const postToken = React.useCallback(() => {
    const contentWindow = iframeRef.current?.contentWindow;
    if (!iframeOrigin || !sessionMessage || !contentWindow) return;
    if (!sessionMessage.dataContext || !canPostToken()) return;

    contentWindow.postMessage(sessionMessage, iframeOrigin);
    setIsReady(true);
  }, [iframeOrigin, sessionMessage, canPostToken]);

  const postTheme = React.useCallback(() => {
    const contentWindow = iframeRef.current?.contentWindow;
    if (!iframeOrigin || !contentWindow) return;
    const message: SEmbedThemeMessage = { type: "S_EMBED_THEME", theme };
    contentWindow.postMessage(message, iframeOrigin);
  }, [iframeOrigin, theme]);

  // A pick in S's menu changes the theme here; S applies it when BI sends it.
  React.useEffect(() => {
    if (hasLoaded) postTheme();
  }, [hasLoaded, postTheme]);

  // Reset when switching embed surfaces, then redo the handshake.
  React.useEffect(() => {
    setIsReady(false);
    setHasLoaded(false);
    setStarted(false);
  }, [iframeSrc]);

  // This effect listens for trusted messages from the S iframe. If S says “ready”, we send the token. If S says “navigation changed”, we remember the path and maybe move the BI host tab.
  React.useLayoutEffect(() => {
    if (!iframeOrigin) return;

    function handleMessage(event: MessageEvent) {
      if (event.origin !== iframeOrigin || event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (consumeSEmbedAnalyticsMessage(event.data)) {
        return;
      }

      if (!isSEmbedHostMessage(event.data)) return;
      const data = event.data;
      if (data?.type === "S_EMBED_READY") {
        setStarted(true);
        // A reloaded iframe may carry an older `?theme`.
        postTheme();
        postToken();
        return;
      }

      if (data?.type === "S_EMBED_NAVIGATION_CHANGE" && typeof data.path === "string") {
        if (!config) return;
        try {
          const hostPath = sNavigationHostPath(data.path, config);
          if (!hostPath) return;
          // S reports its page again on re-renders; keep BI's open sign-out dialog (`?logout`).
          const next = new URL(hostPath, window.location.origin);
          if (new URLSearchParams(currentLocation.current.search).has("logout")) {
            next.searchParams.set("logout", "");
          }
          navigate(`${next.pathname}${next.search}${next.hash}`, { replace: true });
        } catch {
          /* Ignore malformed iframe URLs. */
        }
        return;
      }

      if (data.type === "S_EMBED_LINK") {
        try {
          const hostPath = sHostSettingsPath(data.url, window.location.origin);
          // Sign out confirms over S instead of opening Settings, which non-root users cannot use.
          if (hostPath && new URL(hostPath, window.location.origin).searchParams.has("logout")) {
            const search = new URLSearchParams(currentLocation.current.search);
            search.set("logout", "");
            navigate({ pathname: currentLocation.current.pathname, search: `?${search}` });
          } else if (hostPath) {
            navigate(hostPath);
          }
        } catch {
          /* Ignore malformed menu links. */
        }
      }
      if (data.type === "S_EMBED_THEME_CHANGE") {
        // S already shows the pick; saving it here re-themes BI and confirms it to S.
        useThemeStore.getState().setTheme(data.theme);
      }
      if (data.type === "S_EMBED_PICKER_SELECT" && data.key === "language") {
        try {
          localStorage.setItem(LANGUAGE_STORAGE_KEY, data.optionId === "ar" ? "ar" : "en");
        } catch {
          return; // Without storage the reload would come back in the old language.
        }
        // The host URL follows S's route, so a reload reopens this page with a new session.
        window.location.reload();
      }
      if (data.type === "S_EMBED_PICKER_SELECT" && data.key === "business") {
        void onBusinessSelect(data.optionId).catch(() => {
          /* The host displays switch errors. */
        });
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [iframeOrigin, navigate, postToken, postTheme, config, onBusinessSelect]);

  React.useEffect(() => {
    if (embedSession.isFetching) setIsReady(false);
    if (hasLoaded && !embedSession.isFetching) postToken();
  }, [embedSession.isFetching, hasLoaded, postToken]);

  // S that never starts would otherwise leave a spinner with no way out (no S menu, no Sign out).
  React.useEffect(() => {
    setStalled(false);
    if (!hasLoaded || started) return;
    const timer = setTimeout(() => setStalled(true), S_START_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [hasLoaded, started]);

  return {
    isReady,
    stalled,
    iframeProps: {
      ref: iframeRef,
      src: iframeSrc,
      onLoad: () => setHasLoaded(true),
      allow: "clipboard-write",
      style: !isReady ? { visibility: "hidden", position: "absolute" } : undefined,
    },
  };
}

function withoutLogout(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("logout");
  const query = params.toString();
  return query ? `?${query}` : "";
}
